<template>
  <v-container>
    <toolbar mode="file" title="Create" view="list" />
    <v-form ref="form" method="post" enctype="multipart/form-data" @submit.prevent="onSubmit">
      <v-card flat>
        <v-tabs v-model="tab">
          <v-tab href="#file">File</v-tab>
          <v-tab href="#database">Database</v-tab>
          <v-tab href="#api">API</v-tab>
        </v-tabs>
        <v-divider></v-divider>
        <v-tabs-items v-model="tab">
          <v-tab-item value="file">
                <v-card-text>
                  <v-file-input
                    v-model="file.obj"
                    show-size
                    hint="Attach a CSV or Excel (XLS, XLSX) formatted file"
                    :prepend-icon="null"
                    label="Choose a file"
                    accept=".csv,.xls,.xlsx"
                    required clearable
                    :rules="file.rules"
                  ></v-file-input>
                  <v-text-field
                    label="Name of file"
                    v-model="file.name"
                    hint="Provide a human-readable name for this file"
                    required clearable
                    :rules="[v => !!v || 'Name is required']"
                  ></v-text-field>
                  <v-autocomplete
                    :loading="loading"
                    label="Unique well identifier"
                    :items="file.identifier.options"
                    v-model="file.identifier.selected"
                    hint="Check name capitalization and spelling"
                    required clearable
                    :rules="[v => !!v || 'Identifier is required']"
                  />
                </v-card-text>
          </v-tab-item>
          <v-tab-item value="database">
            <v-card-text>
                  <v-autocomplete
                    label="Select a database connection"
                    v-model="database.connections.selected"
                    :items="database.connections.options"
                    :rules="database.rules"
                    return-object clearable
                  />
                  <v-autocomplete
                    label="Select a table or view for import"
                    v-model="database.tables.selected"
                    :items="database.tables.options"
                    :rules="database.rules" clearable
                    :disabled="database.tables.options.length == 0"
                  />
                  <v-autocomplete
                    label="Select column(s) to import"
                    chips clearable
                    v-model="database.columns.selected"
                    :items="database.columns.options"
                    :rules="database.rules"
                    multiple
                    :disabled="database.columns.options.length == 0"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item ripple @click="toggle">
                        <v-list-item-action>
                          <v-icon :color="database.columns.selected.length > 0 ? 'blue darken-1' : ''">
                            {{ icon }}
                          </v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>
                            Select All
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-2"></v-divider>
                    </template>
                    <template v-slot:selection="{ item, index }">
                      <v-chip v-if="index === 0">
                        <span>{{ item.text }}</span>
                      </v-chip>
                      <span v-if="index === 1" class="grey--text text-caption">(+{{ database.columns.selected.length - 1 }} others)</span>
                    </template>
                    >
                  </v-autocomplete>
                  <v-autocomplete
                    label="Unique well identifier"
                    :items="database.columns.options"
                    v-model="database.identifier"
                    hint="Check name capitalization and spelling"
                    required clearable
                    :disabled="database.columns.options.length == 0"
                    :rules="[v => !!v || 'Identifier is required']"
                  />
            </v-card-text>
          </v-tab-item>
          <v-tab-item value="api">
            <v-card-text>
                  <v-autocomplete
                    label="Select a connection"
                    v-model="api.connections.selected"
                    :items="api.connections.options"
                    :rules="api.rules"
                    return-object clearable
                  />
                  <v-autocomplete
                    label="Select a version"
                    v-model="api.versions.selected"
                    :items="api.versions.options"
                    :rules="api.rules"
                    return-object clearable
                  />
                  <v-autocomplete
                    label="Select a scope"
                    v-model="api.scopes.selected"
                    :items="api.scopes.options"
                    :rules="api.rules"
                    return-object clearable
                  />
                  <v-autocomplete
                    label="Select a basin"
                    v-model="api.basins.selected"
                    :items="api.basins.options"
                    :rules="api.rules"
                    return-object clearable
                  />
                  
                  <v-autocomplete
                    label="Select type of file to import"
                    v-model="api.types.selected"
                    :items="api.types.options"
                    :rules="api.rules" 
                    clearable>
                  </v-autocomplete>

                  <v-autocomplete
                    label="Select column(s) to import"
                    v-model="api.columns.selected"
                    :items="api.columns.options"
                    :rules="api.rules" 
                    multiple clearable>
                    <template v-slot:selection="{ item, index }">
                      <v-chip v-if="index === 0">
                        <span>{{ item.text }}</span>
                      </v-chip>
                      <span v-if="index === 1" class="grey--text text-caption">
                        (+{{ api.columns.selected.length - 1 }} others)
                      </span>
                    </template>
                  </v-autocomplete>
                  <v-autocomplete
                    label="Unique well identifier"
                    :items="api.columns.selected"
                    v-model="api.identifier"
                    hint="Check name capitalization and spelling"
                    required clearable
                    :disabled="api.columns.options.length == 0"
                    :rules="[v => !!v || 'Identifier is required']"
                  />
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
        <v-card-actions>
          <v-btn depressed class="success" type="submit" :disabled="!isValid" :loading="working">Create</v-btn>
          <v-btn text type="button" @click="$router.go(-1)">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-container>
</template>

<script>
import XLSX from "xlsx"
import toolbar from "@/components/toolbar"
import Files from "@/api/files"
import Connections from "@/api/connections"
export default {
  name: "FileCreate",
  components: {
    toolbar
  },
  computed: {
    selectAll() {
      return this.database.columns.selected.length === this.database.columns.options.length
    },
    selectSome() {
      return this.database.columns.selected.length > 0 && !this.selectAll
    },
    icon() {
      if (this.selectAll) return "mdi-close-box"
      if (this.selectSome) return "mdi-minus-box"
      return "mdi-checkbox-blank-outline"
    },
    isValid() {
      if (this.tab == "file") {
        return !!this.file.obj && !!this.file.name && !!this.file.identifier.selected
      } else if (this.tab == "database") {
        return (
          !!this.online &&
          !!this.database.connections.selected &&
          !!this.database.tables.selected &&
          !!this.database.columns.selected &&
          !!this.database.identifier
        )
      } else if (this.tab == "api") {
        return (
          !!this.online &&
          !!this.api.connections.selected &&
          !!this.api.types.selected &&
          !!this.api.columns.selected &&
          !!this.api.identifier
        )
      }
      return false
    }
  },
  watch: {
    "file.obj": function() {
      if (this.file.name == null) this.file.name = this.file.obj.name.split(".")[0]
      this.extractColumns()
    },
    "database.connections.selected": function(value) {
      if (!!value) {
        this.setBusy(true)
        this.database.tables.options = []
        Connections.tables(this.database.connections.selected.value)
          .then(response => {
            response.data.forEach(table => {
              this.database.tables.options.push({ text: table, value: table })
            })
          })
          .finally(() => this.setBusy(false))
      }
    },
    "database.tables.selected": function(value) {
      this.setBusy(true)
      this.database.columns.options = []
      Connections.columns(this.database.connections.selected.value, this.database.tables.selected)
        .then(response => {
          response.data.forEach(column => {
            this.database.columns.options.push({ text: column, value: column })
          })
          this.database.columns.selected = response.data
        })
        .finally(() => this.setBusy(false))
    },
    "api.connections.selected": function(value) {
      this.api.columns.options = []
      
      Connections.versions(this.api.connections.selected.value).then(function(response) {
        this.api.versions.options = response.data
      }.bind(this))

      Connections.scopes(this.api.connections.selected.value).then(function(response) {
        this.api.scopes.options = response.data
      }.bind(this))

      Connections.basins(this.api.connections.selected.value).then(function(response) {
        this.api.basins.options = response.data
      }.bind(this))

    },
    "api.types.selected": function(value) {
      Connections.columns(this.api.connections.selected.value, value).then( response => {
        this.api.columns.options = [] 
        response.data.forEach(column => {
          this.api.columns.options.push({ text: column, value: column })
        })
        this.api.columns.selected = response.data
      })
    }
  },
  data() {
    return {
      tab: "file",
      loading: false,
      working: false,
      file: {
        obj: null,
        name: null,
        identifier: {
          selected: null,
          options: []
        },
        rules: [
          value => !!value || "File is required",
          value => (value && ["csv", "xls", "xlsx"].includes(value.name.split(".")[1].toLowerCase())) || "Input must a CSV or Excel type file"
        ]
      },
      database: {
        connected: false,
        identifier: null,
        rules: [value => !!value || "Field is required"],
        connections: {
          selected: null,
          options: []
        },
        tables: {
          selected: null,
          options: []
        },
        columns: {
          selected: [],
          options: []
        },
        filters: null
      },
      api: {
        connections: {
          selected: null,
          options: []
        },
        versions: {
          selected: null,
          options: []
        },
        scopes: {
          selected: null,
          options: []
        },
        basins: {
          selected: null,
          options: []
        },
        types: {
          selected: [],
          options: [
            'Completion', 'Production'
          ]
        },
        columns: {
          selected: [],
          options: []
        },
        identifier: null,
        rules: [value => !!value || "Field is required"],
      }
    }
  },
  mounted() {
    this.setBusy(true)
    Connections.list().then(response => {
      response.data.forEach(conn => {
        if (conn.driver.type == 'DATABASE') {
          this.database.connections.options.push({ text: `${conn.username}@${conn.server}/${conn.schema}`, value: conn.id })
        } else if (conn.driver.type == 'REST API') {
          this.api.connections.options.push({ text: `${conn.username}@${conn.server}/${conn.schema}`, value: conn.id })
        }
      })
    })
    .finally(() => this.setBusy(false))
  },
  methods: {
    onSubmit() {
      this.working = true

      if (this.tab == "file") {
        let formData = new FormData()
        formData.append("name", this.file.name)
        formData.append("file", this.file.obj)
        formData.append("identifier", this.file.identifier.selected)
        formData.append("owner", this.user.id)

        Files.create(formData)
          .then(_ => {
            this.$router.push({ name: "fileList" })
          })
          .finally(() => (this.working = false))
      } else if (this.tab == "database") {
        Files.create({
          name: this.database.tables.selected,
          connection: this.database.connections.selected.value,
          table: this.database.tables.selected,
          columns: this.database.columns.selected,
          identifier: this.database.identifier,
          owner: this.user.id
        })
          .then(_ => {
            this.$router.push({ name: "fileList" })
          })
          .finally(() => (this.working = false))
      } else if (this.tab == "api") {
        Files.create({
          name: `${this.api.types.selected} for ${this.api.basins.selected} basin`,
          connection: this.api.connections.selected.value,
          table: `${this.api.types.selected}/${this.api.basins.selected}`,
          columns: this.api.columns.selected,
          identifier: this.api.identifier,
          owner: this.user.id
        })
        .then(_ => {
          this.$router.push({ name: "fileList" })
        })
        .finally(() => (this.working = false))
      }
    },
    toggle() {
      this.$nextTick(() => {
        if (this.selectAll) {
          this.database.columns.selected = []
        } else {
          this.database.columns.selected = this.database.columns.options.slice()
        }
      })
    },
    extractColumns() {
      if (!this.file.obj) return;
      
      const fileName = this.file.obj.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      
      var reader = new FileReader()
      reader.onload = e => {
        try {
          let wb;
          const result = e.target.result;
          
          if (fileExtension === 'csv') {
            // For CSV files, read as text and parse with XLSX
            wb = XLSX.read(result, { type: "string", sheetRows: 1 })
          } else if (fileExtension === 'xls' || fileExtension === 'xlsx') {
            // For Excel files, read as array buffer
            wb = XLSX.read(result, { type: "array", sheetRows: 1 })
          } else {
            return;
          }
          
          /* Get first worksheet */
          const wsname = wb.SheetNames[0]
          const ws = wb.Sheets[wsname]
          /* Convert array of arrays */
          const data = XLSX.utils.sheet_to_json(ws, { header: 1 })
          this.file.identifier.options = data[0] || []
        } catch (error) {
          // Optionally show user-friendly error message
          this.$store.commit('showSnackbar', {
            text: 'Error reading file. Please check if the file is corrupted or in the correct format.',
            color: 'error'
          });
        }
      }
      
      // Use appropriate reading method based on file type
      if (fileExtension === 'csv') {
        reader.readAsText(this.file.obj)
      } else if (fileExtension === 'xls' || fileExtension === 'xlsx') {
        reader.readAsArrayBuffer(this.file.obj)
      }
    }
  }
}
</script>
